import React, { useMemo, useState } from 'react';
import clsx from 'clsx';

import { MessageErrorIcon, useChannelStateContext } from 'stream-chat-react';
import { MessageDeleted as DefaultMessageDeleted } from 'stream-chat-react';
import { CustomMessageBlocked as DefaultMessageBlocked } from './CustomMessageBlocked';
import { MessageOptions as DefaultMessageOptions } from 'stream-chat-react';
import { MessageRepliesCountButton as DefaultMessageRepliesCountButton } from 'stream-chat-react';
import { MessageStatus as DefaultMessageStatus } from 'stream-chat-react';
import { MessageTimestamp as DefaultMessageTimestamp } from 'stream-chat-react';
import { StreamedMessageText as DefaultStreamedMessageText } from 'stream-chat-react';
import { isDateSeparatorMessage } from 'stream-chat-react';
import { CustomMessageIsThreadReplyInChannelButtonIndicator as DefaultMessageIsThreadReplyInChannelButtonIndicator } from './CustomMessageIsThreadReplyInChannelButtonIndicator';
import { ReminderNotification as DefaultReminderNotification } from 'stream-chat-react';
import { useMessageReminder } from 'stream-chat-react';
import {
  areMessageUIPropsEqual,
  isMessageBlocked,
  isMessageBounced,
  isMessageEdited,
  messageHasAttachments,
  messageHasReactions,
} from 'stream-chat-react';

import { Avatar as DefaultAvatar } from 'stream-chat-react';
import { Attachment as DefaultAttachment } from 'stream-chat-react';
import { EditMessageModal } from 'stream-chat-react';
import { Poll } from 'stream-chat-react';
import { ReactionsList as DefaultReactionList } from 'stream-chat-react';
import { useComponentContext } from 'stream-chat-react';
import type { MessageContextValue } from 'stream-chat-react';
import { useMessageContext } from 'stream-chat-react';

import { useChatContext } from 'stream-chat-react';

import type { MessageUIComponentProps } from 'stream-chat-react';
import { ChatChannelType } from '../../../models/ChatChannel';
import { authorColor, formatAmount } from '../../../utils/Utils';
import { walletRepository } from '../../../repositories/WalletRepository';
import { CustomMessageText } from './CustomMessageText';
import { LightningAmount } from '../../../components/LightningAmount';

type MessageSimpleWithContextProps = MessageContextValue;

const MessageSimpleWithContext = (props: MessageSimpleWithContextProps) => {
  const {
    additionalMessageInputProps,
    editing,
    endOfGroup,
    firstOfGroup,
    groupedByUser,
    handleAction,
    handleOpenThread,
    handleRetry,
    highlighted,
    isMessageAIGenerated,
    isMyMessage,
    message,
    onUserClick,
    onUserHover,
    renderText,
    threadList,
  } = props;
  const { client } = useChatContext('MessageSimple');
  const reminder = useMessageReminder(message.id);
  const { channel } = useChannelStateContext();

  const {
    Attachment = DefaultAttachment,
    Avatar = DefaultAvatar,
    MessageOptions = DefaultMessageOptions,
    // TODO: remove this "passthrough" in the next
    // major release and use the new default instead
    MessageActions = MessageOptions,
    MessageBlocked = DefaultMessageBlocked,
    MessageDeleted = DefaultMessageDeleted,
    MessageIsThreadReplyInChannelButtonIndicator = DefaultMessageIsThreadReplyInChannelButtonIndicator,
    MessageRepliesCountButton = DefaultMessageRepliesCountButton,
    MessageStatus = DefaultMessageStatus,
    MessageTimestamp = DefaultMessageTimestamp,
    ReactionsList = DefaultReactionList,
    ReminderNotification = DefaultReminderNotification,
    StreamedMessageText = DefaultStreamedMessageText,
    PinIndicator,
  } = useComponentContext('MessageSimple');
  const hasAttachment = messageHasAttachments(message);
  const hasReactions = messageHasReactions(message);
  const isAIGenerated = useMemo(
    () => isMessageAIGenerated?.(message),
    [isMessageAIGenerated, message],
  );
  const finalAttachments = useMemo(
    () =>
      !message.shared_location && !message.attachments
        ? []
        : !message.shared_location
          ? message.attachments
          : [message.shared_location, ...(message.attachments ?? [])],
    [message],
  );

  if (isDateSeparatorMessage(message)) {
    return null;
  }

  if (message.deleted_at || message.type === 'deleted') {
    return <MessageDeleted message={message} />;
  }

  if (isMessageBlocked(message)) {
    return <MessageBlocked />;
  }

  const showMetadata = !groupedByUser || endOfGroup;
  const showReplyCountButton = !threadList && !!message.reply_count;
  const showIsReplyInChannel =
    !threadList && message.show_in_channel && message.parent_id;
  const allowRetry = message.status === 'failed' && message.error?.status !== 403;
  const isBounced = isMessageBounced(message);
  const isEdited = isMessageEdited(message) && !isAIGenerated;

  let handleClick: (() => void) | undefined = undefined;

  if (allowRetry) {
    handleClick = () => handleRetry(message);
  }

  const handleClickMessage = (event: React.MouseEvent) => {
    // Don't zap if clicking on interactive elements
    const target = event.target as HTMLElement;
    const isInteractiveElement = target.closest('a, button, input, select, textarea, [role="button"], [onclick], [data-clickable]');
    
    if (isInteractiveElement) {
      return;
    }
  };

  const rootClassName = clsx(
    'str-chat__message str-chat__message-simple',
    `str-chat__message--${message.type}`,
    `str-chat__message--${message.status}`,
    isMyMessage()
      ? 'str-chat__message--me str-chat__message-simple--me'
      : 'str-chat__message--other',
    message.text ? 'str-chat__message--has-text' : 'has-no-text',
    {
      'str-chat__message--has-attachment': hasAttachment,
      'str-chat__message--highlighted': highlighted,
      'str-chat__message--pinned pinned-message': message.pinned,
      'str-chat__message--with-reactions': hasReactions,
      'str-chat__message-send-can-be-retried':
        message?.status === 'failed' && message?.error?.status !== 403,
      'str-chat__message-with-thread-link': showReplyCountButton || showIsReplyInChannel,
      'str-chat__virtual-message__wrapper--end': endOfGroup,
      'str-chat__virtual-message__wrapper--first': firstOfGroup,
      'str-chat__virtual-message__wrapper--group': groupedByUser,
    },
  );

  const poll = message.poll_id && client.polls.fromState(message.poll_id);

  const showAuthorName = !isMyMessage() && !!message.user && (channel.type === ChatChannelType.Group || channel.type === ChatChannelType.Event)

  let msatsZappedTotal = message.zapData?.map((data: any) => data.amountMsat).reduce((a: any, b: any) => a + b, 0);
  if(isNaN(msatsZappedTotal)) {
    msatsZappedTotal = 0;
  }

  return (
    <>
      {editing && (
        <EditMessageModal additionalMessageInputProps={additionalMessageInputProps} />
      )}
      {
        <div className={rootClassName} key={message.id}>
          {PinIndicator && <PinIndicator />}
          {!!reminder && <ReminderNotification reminder={reminder} />}
          {message.user && (
            <Avatar
              image={message.user.image}
              name={message.user.name || message.user.id}
              onClick={onUserClick}
              onMouseOver={onUserHover}
              user={message.user}
            />
          )}
          <div
            className={clsx('str-chat__message-inner', {
              'str-chat__simple-message--error-failed': allowRetry || isBounced,
            })}
            data-testid='message-inner'
            onClick={handleClick}
            onKeyUp={handleClick}
          >
            <MessageActions />
            <div className='str-chat__message-reactions-host'>
              {hasReactions && <ReactionsList reverse />}
            </div>
            <div className={`str-chat__message-bubble ${showAuthorName ? 'with-author-name' : ''}`}>
              {showAuthorName && (
                <div 
                  className='px-4 pt-1 font-semibold text-sm cursor-pointer str-chat__message-author-name' 
                  style={{ color: authorColor(message.user?.id ?? '') }}
                  onClick={onUserClick}
                >
                  {message.user?.name || message.user?.id}
                </div>
              )}
              {poll && <Poll poll={poll} />}
              {finalAttachments?.length && !message.quoted_message ? (
                <Attachment actionHandler={handleAction} attachments={finalAttachments} />
              ) : null}
              {isAIGenerated ? (
                <StreamedMessageText message={message} renderText={renderText} />
              ) : (
                <CustomMessageText message={message} renderText={renderText} onClickMessage={handleClickMessage} isMyMessage={isMyMessage()} />
              )}
              <MessageErrorIcon />
            </div>
          </div>
          {showReplyCountButton && (
            <MessageRepliesCountButton
              onClick={handleOpenThread}
              reply_count={message.reply_count}
            />
          )}
          {showIsReplyInChannel && <MessageIsThreadReplyInChannelButtonIndicator />}
            <LightningAmount amount={msatsZappedTotal} />
        </div>
      }
    </>
  );
};

const MemoizedMessageSimple = React.memo(
  MessageSimpleWithContext,
  areMessageUIPropsEqual,
) as typeof MessageSimpleWithContext;

/**
 * The default UI component that renders a message and receives functionality and logic from the MessageContext.
 */
export const CustomMessage = (props: MessageUIComponentProps) => {
  const messageContext = useMessageContext('MessageSimple');

  return <MemoizedMessageSimple {...messageContext} {...props} />;
};